import React, { useState, useEffect, useRef } from 'react';
import {
  getGoogleCampaignMappings,
  createGoogleCampaignMapping,
  deleteGoogleCampaignMapping,
  getGoogleAdsCampaigns,
  getAdminSchools,
} from '@/actions/reportsAdmin';
import { Search, Trash2, Plus, X, BarChart2 } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface GoogleCampaignMapping {
  map_id: number;
  campaign_id: string;
  campaign_name: string;
  client_id: number;
  client_name: string;
  slate_name: string;
  slate_id: number;
  cityName: string;
  areaName: string;
}

interface GoogleCampaign {
  campaign_id: string;
  campaign_name: string;
  status: string;
}

interface SchoolOption {
  slate_id: number;
  slate_name: string;
  cityName: string;
  areaName: string;
  client_id: number;
}

const GoogleCampaignMappingsAdmin: React.FC = () => {
  const [mappings, setMappings] = useState<GoogleCampaignMapping[]>([]);
  const [campaigns, setCampaigns] = useState<GoogleCampaign[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<GoogleCampaign | null>(null);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [showCampaignSuggestions, setShowCampaignSuggestions] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const campaignRef = useRef<HTMLDivElement>(null);
  const schoolRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMappings();
    fetchCampaigns();
    fetchSchools();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (campaignRef.current && !campaignRef.current.contains(e.target as Node)) setShowCampaignSuggestions(false);
      if (schoolRef.current && !schoolRef.current.contains(e.target as Node)) setShowSchoolSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchMappings = async () => {
    try {
      setIsLoading(true);
      const res = await getGoogleCampaignMappings();
      if (res.success) setMappings((res.data as GoogleCampaignMapping[]) || []);
      else showToast('Failed to fetch mappings', 'error');
    } catch {
      showToast('Failed to fetch mappings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await getGoogleAdsCampaigns();
      if (res.success) setCampaigns((res.data as GoogleCampaign[]) || []);
    } catch {
      console.error('Failed to fetch Google Ads campaigns');
    }
  };

  const fetchSchools = async () => {
    try {
      const res = await getAdminSchools();
      if (res.success) setSchools((res.data as SchoolOption[]) || []);
    } catch {
      console.error('Failed to fetch schools');
    }
  };

  const handleAddMapping = async () => {
    if (!selectedCampaign) { showToast('Please select a campaign', 'error'); return; }
    if (!selectedSchool) { showToast('Please select a school', 'error'); return; }
    if (!selectedSchool.client_id) { showToast('Could not find client ID for this school', 'error'); return; }

    setIsSubmitting(true);
    try {
      const res = await createGoogleCampaignMapping({
        campaign_id: selectedCampaign.campaign_id,
        campaign_name: selectedCampaign.campaign_name,
        client_id: selectedSchool.client_id,
      });
      if (res.success) {
        showToast('Mapping created successfully', 'success');
        setSelectedCampaign(null);
        setCampaignSearchQuery('');
        setSelectedSchool(null);
        setSchoolSearchQuery('');
        setShowAddForm(false);
        fetchMappings();
      } else {
        showToast(res.message || 'Failed to create mapping', 'error');
      }
    } catch {
      showToast('Failed to create mapping', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMapping = async (mapId: number) => {
    if (!confirm('Are you sure you want to delete this mapping?')) return;
    try {
      const res = await deleteGoogleCampaignMapping(mapId);
      if (res.success) { showToast('Mapping deleted', 'success'); fetchMappings(); }
      else showToast('Failed to delete mapping', 'error');
    } catch {
      showToast('Failed to delete mapping', 'error');
    }
  };

  const filteredMappings = mappings.filter(m =>
    m.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.campaign_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.slate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCampaigns = campaigns.filter(c =>
    c.campaign_name.toLowerCase().includes(campaignSearchQuery.toLowerCase())
  );

  const filteredSchools = schools.filter(s =>
    s.slate_name.toLowerCase().includes(schoolSearchQuery.toLowerCase()) ||
    (s.cityName && s.cityName.toLowerCase().includes(schoolSearchQuery.toLowerCase())) ||
    (s.areaName && s.areaName.toLowerCase().includes(schoolSearchQuery.toLowerCase()))
  );

  const statusColor = (status: string) =>
    status === 'ENABLED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <BarChart2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">Google Ads Campaign Mappings</h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                  {mappings.length}
                </span>
              </div>
              <p className="text-xs text-gray-500">Map Google Ads campaigns to schools for spend tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search mappings..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-md hover:bg-brand-hover"
            >
              <Plus size={16} />
              Add Mapping
            </button>
          </div>
        </div>
      </div>

      {/* Add Form Modal — overflow-visible so dropdowns escape the container */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 bg-brandColor text-white rounded-t-lg">
              <h2 className="text-lg font-semibold">Add Google Ads Campaign Mapping</h2>
              <button onClick={() => setShowAddForm(false)} className="text-white hover:text-gray-200">
                <X size={20} />
              </button>
            </div>

            {/* Modal body — overflow-visible so dropdowns are not clipped */}
            <div className="p-6 space-y-4 overflow-visible">

              {/* Campaign selector */}
              <div ref={campaignRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads Campaign</label>
                <input
                  type="text"
                  value={campaignSearchQuery}
                  onChange={e => { setCampaignSearchQuery(e.target.value); setShowCampaignSuggestions(true); if (!e.target.value) setSelectedCampaign(null); }}
                  onFocus={() => setShowCampaignSuggestions(true)}
                  placeholder="Search campaign name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
                />
                {showCampaignSuggestions && campaignSearchQuery && filteredCampaigns.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[9999]">
                    {filteredCampaigns.slice(0, 15).map(c => (
                      <div
                        key={c.campaign_id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setSelectedCampaign(c); setCampaignSearchQuery(c.campaign_name); setShowCampaignSuggestions(false); }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColor(c.status)}`}>{c.status}</span>
                          <span className="font-medium text-gray-900">{c.campaign_name}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">ID: {c.campaign_id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* School selector */}
              <div ref={schoolRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                <input
                  type="text"
                  value={schoolSearchQuery}
                  onChange={e => { setSchoolSearchQuery(e.target.value); setShowSchoolSuggestions(true); if (!e.target.value) setSelectedSchool(null); }}
                  onFocus={() => setShowSchoolSuggestions(true)}
                  placeholder="Search and select a school..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
                />
                {showSchoolSuggestions && schoolSearchQuery && filteredSchools.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[9999]">
                    {filteredSchools.slice(0, 10).map(s => (
                      <div
                        key={s.slate_id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setSelectedSchool(s); setSchoolSearchQuery(s.slate_name); setShowSchoolSuggestions(false); }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        <div className="font-medium text-gray-900">{s.slate_name}</div>
                        {(s.areaName || s.cityName) && (
                          <div className="text-xs text-gray-500">{[s.areaName, s.cityName].filter(Boolean).join(', ')}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
                <Button onClick={handleAddMapping} disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Mapping'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto px-4 mt-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-brandColor text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Campaign ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">City</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Area</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading mappings...</td></tr>
              ) : filteredMappings.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No mappings found</td></tr>
              ) : (
                filteredMappings.map((m, i) => (
                  <tr key={m.map_id} className={`hover:bg-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-4 py-2 text-sm text-gray-600">{m.map_id}</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{m.campaign_name}</td>
                    <td className="px-4 py-2 text-sm">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">{m.campaign_id}</code>
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{m.slate_name || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{m.cityName || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{m.areaName || '-'}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleDeleteMapping(m.map_id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete mapping"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-4 py-3 border-t bg-gray-50">
        <div className="text-sm text-gray-600">Showing {filteredMappings.length} of {mappings.length} mappings</div>
      </div>
    </div>
  );
};

export default GoogleCampaignMappingsAdmin;
